import React from "react";
import { Helmet } from "react-helmet";

const SEO = ({
  title,
  description,
  image,
  url,
  type = "website",
  siteName = "Animac Metaverse",
  author,
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  content
}) => {
  // Auto-generate title and description from content if not provided
  const generateFromContent = (content) => {
    if (!content) return { title: '', description: '' };

    const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 10);
    const description = sentences[0]?.trim().substring(0, 155) + (sentences[0]?.length > 155 ? '...' : '');

    const lines = content.split('\n').filter(line => line.trim());
    const title = lines[0]?.trim().substring(0, 60) + (lines[0]?.length > 60 ? '...' : '');

    return { title, description };
  };

  const autoGenerated = content ? generateFromContent(content) : { title: '', description: '' };

  // Use provided values or auto-generated fallbacks
  const finalTitle = title || autoGenerated.title || `${siteName} - ${type === 'article' ? 'Article' : 'Page'}`;
  const finalDescription = description || autoGenerated.description || `Discover amazing content on ${siteName}`;

  // Ensure we have absolute URLs for images
  const getAbsoluteUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `https://animac-metaverse.vercel.app${url}`;
  };

  // Fallback to default image if none provided
  const getOgImage = (img) => {
    const absUrl = getAbsoluteUrl(img);
    if (absUrl) return absUrl;
    return 'https://animac-metaverse.vercel.app/assets/animac-preview-logo.svg';
  };

  const absoluteImage = getOgImage(image);
  const absoluteUrl = url ? (url.startsWith('http') ? url : `https://animac-metaverse.vercel.app${url}`) : 'https://animac-metaverse.vercel.app';

  return (
    <Helmet>
      {/* Standard Meta */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="author" content={author || "Animac Metaverse"} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={absoluteUrl} />

      {/* Open Graph / Universal Social Media */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:alt" content={finalTitle || "Animac Metaverse - Entertainment Content"} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/svg+xml" />
      <meta property="og:url" content={absoluteUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific meta tags */}
      {type === "article" && (
        <>
          {author && <meta property="article:author" content={author} />}
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {section && <meta property="article:section" content={section} />}
          {tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@animacmetaverse" />
      <meta name="twitter:creator" content="@animacmetaverse" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:image:alt" content={finalTitle} />

      {/* WhatsApp */}
      <meta property="og:image:secure_url" content={absoluteImage} />
      <meta property="wa:title" content={finalTitle} />
      <meta property="wa:description" content={finalDescription} />
      <meta property="wa:image" content={absoluteImage} />

      {/* LinkedIn */}
      <meta property="linkedin:owner" content="animac-metaverse" />
      <meta property="linkedin:card" content="summary_large_image" />

      {/* Discord */}
      <meta name="discord:title" content={finalTitle} />
      <meta name="discord:description" content={finalDescription} />
      <meta name="discord:image" content={absoluteImage} />

      {/* Telegram */}
      {/* Uses Open Graph - no additional tags needed */}

      {/* Pinterest */}
      <meta name="pinterest-rich-pin" content="true" />
      <meta name="pinterest:title" content={finalTitle} />
      <meta name="pinterest:description" content={finalDescription} />
      <meta name="pinterest:image" content={absoluteImage} />

      {/* Reddit */}
      <meta name="reddit:title" content={finalTitle} />
      <meta name="reddit:description" content={finalDescription} />

      {/* Slack */}
      <meta name="slack:title" content={finalTitle} />
      <meta name="slack:description" content={finalDescription} />
      <meta name="slack:image" content={absoluteImage} />

      {/* Microsoft Teams */}
      {/* Uses Open Graph - no additional tags needed */}

      {/* Apple Messages (iMessage) */}
      {/* Uses Open Graph - no additional tags needed */}

      {/* Instagram (for stories and reels) */}
      <meta property="instagram:title" content={finalTitle} />
      <meta property="instagram:description" content={finalDescription} />

      {/* TikTok */}
      <meta name="tiktok:title" content={finalTitle} />
      <meta name="tiktok:description" content={finalDescription} />
      <meta name="tiktok:image" content={absoluteImage} />

      {/* Snapchat */}
      <meta name="snapchat:title" content={finalTitle} />
      <meta name="snapchat:description" content={finalDescription} />
      <meta name="snapchat:image" content={absoluteImage} />

      {/* YouTube (Community posts) */}
      <meta name="youtube:title" content={finalTitle} />
      <meta name="youtube:description" content={finalDescription} />

      {/* Twitch */}
      {/* Uses Open Graph - no additional tags needed */}

      {/* Mastodon */}
      <meta name="mastodon:title" content={finalTitle} />
      <meta name="mastodon:description" content={finalDescription} />

      {/* Bluesky */}
      <meta name="bluesky:title" content={finalTitle} />
      <meta name="bluesky:description" content={finalDescription} />
      <meta name="bluesky:image" content={absoluteImage} />

      {/* Threads (Meta) */}
      <meta property="threads:title" content={finalTitle} />
      <meta property="threads:description" content={finalDescription} />

      {/* Facebook */}
      <meta property="fb:app_id" content="YOUR_FB_APP_ID" />
      <meta property="fb:pages" content="YOUR_FB_PAGE_ID" />

      {/* Google+ (Deprecated but good for SEO) */}
      <meta itemprop="name" content={finalTitle} />
      <meta itemprop="description" content={finalDescription} />
      <meta itemprop="image" content={absoluteImage} />

      {/* Additional Universal Meta Tags */}
      <meta name="theme-color" content="#1a1a1a" />
      <meta name="msapplication-TileColor" content="#1a1a1a" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Schema.org structured data for better search engine understanding */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": type === "article" ? "Article" : "WebPage",
          "headline": finalTitle,
          "description": finalDescription,
          "image": absoluteImage,
          "url": absoluteUrl,
          "author": type === "article" && author ? {
            "@type": "Person",
            "name": author
          } : undefined,
          "publisher": {
            "@type": "Organization",
            "name": siteName,
            "logo": {
              "@type": "ImageObject",
              "url": "https://animac-metaverse.vercel.app/assets/animac-preview-logo.svg"
            }
          },
          "datePublished": type === "article" && publishedTime ? publishedTime : undefined,
          "dateModified": type === "article" && modifiedTime ? modifiedTime : undefined,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": absoluteUrl
          },
          // Breadcrumbs for articles
          ...(type === "article" && {
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://animac-metaverse.vercel.app"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": section === "east" ? "East Portal" : section === "west" ? "West Portal" : "Articles",
                  "item": section === "east" ? "https://animac-metaverse.vercel.app/east-portal" :
                         section === "west" ? "https://animac-metaverse.vercel.app/west-portal" :
                         "https://animac-metaverse.vercel.app"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": title,
                  "item": absoluteUrl
                }
              ]
            }
          })
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
